import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, Button, Field, Badge, KV, Center, Spinner, Icon, PageHeader } from "@/components/ui";
import { Topbar } from "@/components/Layout";
import { api } from "@/lib/api";

export default function CarePlan() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const id = params.get("id");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState("");
  const [tasksText, setTasksText] = useState("");
  const [rate, setRate] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [versions, setVersions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, v] = await Promise.all([api.get(`/office/care-plans/${id}`), api.get(`/office/care-plans/${id}/versions`)]);
      setData(d); setVersions(v.versions || []);
      setSummary(d.care_plan.summary || ""); setTasksText((d.care_plan.tasks || []).join("\n")); setRate(String(d.care_plan.rate || ""));
    } catch (e) { setMsg(e.message); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { if (id) load(); }, [id, load]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 3500); };

  const save = async () => { setBusy(true); try { await api.patch(`/office/care-plans/${id}`, { summary, tasks: tasksText.split("\n").map((t) => t.trim()).filter(Boolean), rate: parseFloat(rate) || undefined }); flash("Draft saved."); await load(); } catch (e) { flash(e.message || "Save failed"); } finally { setBusy(false); } };
  const approve = async () => { setBusy(true); try { const r = await api.post(`/office/care-plans/${id}/approve`); flash(`Approved — service starts ${new Date(r.services_start_at).toLocaleString()} (day one unbilled).`); await load(); } catch (e) { flash(e.message || "Approve failed"); } finally { setBusy(false); } };
  const newVersion = async () => { setBusy(true); try { const r = await api.post(`/office/care-plans/${id}/new-version`); nav(`/careplan?id=${r.plan_id}`); } catch (e) { flash(e.message || "Failed"); setBusy(false); } };

  if (loading || !data) return <div className="screen"><Topbar title="Care plan" /><Center><Spinner /></Center></div>;
  const plan = data.care_plan, approved = plan.status === "approved", src = data.source_intake || {};

  return (
    <div className="screen" data-testid="careplan-editor">
      <Topbar title="Review care plan" subtitle={`v${plan.version} · ${plan.status}`} />
      <div className="container-narrow">
        <PageHeader title="Review care plan" subtitle={`v${plan.version} · ${plan.status}`} onBack={() => nav(-1)} />
        {msg ? <div className="toast" data-testid="editor-toast">{msg}</div> : null}
        {plan.status === "needs_review" ? <Badge label="drafted by system — needs review" tone="warning" /> : <Badge label={plan.status} tone="success" />}
        <Card>
          <div className="tag">SOURCE INTAKE ANSWERS (what this draft is based on)</div>
          <KV k="care type" v={String(src.care_type || "—")} mono /><KV k="urgency" v={String(src.urgency || "—")} mono />
          <KV k="schedule" v={(src.schedule || []).join(", ") || "—"} mono /><KV k="budget" v={String(src.budget_band || "—")} mono />
          <KV k="zip" v={String(src.zip || "—")} mono /><KV k="relationship" v={String(src.relationship_to_recipient || "—")} mono />
          {src.prior_reason ? <KV k="prior reason" v={String(src.prior_reason)} mono /> : null}
        </Card>
        <div className="section-label">EDIT DRAFT {approved ? "(locked — approved)" : ""}</div>
        <Field label="Summary" value={summary} onChange={setSummary} multiline testID="edit-summary" />
        <Field label="Tasks (one per line)" value={tasksText} onChange={setTasksText} multiline testID="edit-tasks" />
        <Field label="Rate ($)" value={rate} onChange={(v) => setRate(v.replace(/[^0-9.]/g, ""))} inputMode="decimal" testID="edit-rate" />
        {!approved ? <>
          <Button testID="editor-save" variant="secondary" icon="save" label="Save draft" onClick={save} loading={busy} />
          <div style={{ height: 8 }} />
          <Button testID="editor-approve" icon="check" label="Approve & finalize" onClick={approve} loading={busy} />
          <p className="note">On approval, service is scheduled 24 hours later and day one is an unbilled trial.</p>
        </> : <Card>
          <p className="summary">Approved plans are locked. Service starts {plan.services_start_at ? new Date(plan.services_start_at).toLocaleString() : "—"}.</p>
          <Button testID="new-version" variant="secondary" icon="copy" label="Create new version" onClick={newVersion} loading={busy} />
        </Card>}
        {versions.length > 1 ? <>
          <div className="section-label">VERSION HISTORY</div>
          {versions.map((v) => (
            <div key={v.id} className="between" data-testid={`version-${v.version}`} style={{ padding: "12px 0", borderBottom: "1px solid var(--divider)", cursor: "pointer" }} onClick={() => v.id !== id && nav(`/careplan?id=${v.id}`)}>
              <span style={{ fontFamily: "var(--mono)", fontWeight: 800, color: "var(--brand)" }}>v{v.version}</span>
              <span style={{ flex: 1, marginLeft: 12 }}>{v.status}{v.id === id ? " · viewing" : ""}</span>
              <Icon name="chevron-right" size={16} color="var(--muted)" />
            </div>
          ))}
        </> : null}
      </div>
    </div>
  );
}
